import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

interface Testimonial {
  name: string;
  content: string;
  rating: number;
}

const TestimonialsSection = () => {
  // const testimonials: Testimonial[] = [
  //   {
  //       name: "Krupa Solanki",
  //       content: "I felt very comfortable to be as vulnerable as possible. I felt I was connecting the dots myself which was really nice.",
  //       rating: 5
  //     },
  //   {
  //     name: "Vaishali",
  //     content: "The experience has been incredibly amazing so far. I couldn't have asked for a more understanding therapist who never made me feel judged for anything. In these two years of therapy, I have seen myself grow and become a better person in both my professional and personal life.",
  //     rating: 5
  //   },
  //   {
  //     name: "Mahima Singh",
  //     content: "I love my therapy sessions and I look forward to them! I feel, taking therapy sessions with Aanya helps me think more clearly. She helps me learn to be more compassionate towards myself and allow myself to feel and understand my thoughts and feelings. I’m so grateful to have found such an amazing therapist!",
  //     rating: 5
  //   },

  //   {
  //     name: "Yashvi Peeti",
  //     content: "It's been helpful. It has helped me unpack the patterns I picked up in childhood. Thank you for providing a safe space to do that. Thank you for guiding me, empathising with me and laughing at my jokes.",
  //     rating: 5
  //   },
  //   {
  //       name: 'Anwesha Sen', 
  //       content: 'It has been good. I think validation & acknowledgment of my issues have worked super well in the last 2+ years with Aanya. However, more interventions in terms of dealing with my anxiety outside of therapy and more knowledge for what I am going through helps me make sense of everything given my tendencies to intellectually while also helping my practice the art of feeling, not thinking my feelings. Thank you! ',
  //       rating : 4
  //   },
  // ];

  const testimonials: Testimonial[] = [
    {
      name: "Ananya Sharma",
      content: "I felt very comfortable to be as vulnerable as possible. I felt I was connecting the dots myself which was really nice.",
      rating: 5
    },
    {
      name: "Priya Verma",
      content: "The experience has been incredibly amazing so far. I couldn't have asked for a more understanding therapist who never made me feel judged for anything. In these two years of therapy, I have seen myself grow and become a better person in both my professional and personal life.",
      rating: 5
    },
    {
      name: "Riya Patel",
      content: "I love my therapy sessions and I look forward to them! I feel, taking therapy sessions with Aanya helps me think more clearly. She helps me learn to be more compassionate towards myself and allow myself to feel and understand my thoughts and feelings. I’m so grateful to have found such an amazing therapist!",
      rating: 5
    },
    {
      name: "Sneha Gupta",
      content: "It's been helpful. It has helped me unpack the patterns I picked up in childhood. Thank you for providing a safe space to do that. Thank you for guiding me, empathising with me and laughing at my jokes.",
      rating: 5
    },
    {
      name: "Diya Rao",
      content: "It has been good. I think validation & acknowledgment of my issues have worked super well in the last 2+ years with Aanya. However, more interventions in terms of dealing with my anxiety outside of therapy and more knowledge for what I am going through helps me make sense of everything given my tendencies to intellectually while also helping my practice the art of feeling, not thinking my feelings. Thank you! ",
      rating: 4
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<number>(0);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => (
      (prevIndex + newDirection + testimonials.length) % testimonials.length
    ));
  };

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 8000);

    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, []);

  const renderStars = (rating: number) => {
    return [...Array(rating)].map((_, index) => (
      <span key={index} className="text-golden-yellow">★</span>
    ));
  };

  return (
    <section className="w-full bg-white py-8 sm:py-12 lg:py-16 px-4 relative overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto text-center mb-8 sm:mb-12"
      >
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-500 mb-3 sm:mb-4">
          Client Experiences
        </h2>
        <p className="text-teal-700 text-base sm:text-lg lg:text-xl">
          Real stories from real people on their therapy journey
        </p>
      </motion.div>

      <div className="max-w-4xl mx-auto relative px-4">
        <div className="relative h-[400px] mt-16 md:h-[400px] lg:h-[300px]">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className="absolute w-full"
            >
              <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg relative">
                <Quote className="absolute text-palegola opacity-20 w-12 h-12 sm:w-16 sm:h-16 -top-4 -left-4 sm:-top-6 sm:-left-6" />
                <div className="mb-4 sm:mb-6">
                  {renderStars(testimonials[currentIndex].rating)}
                </div>
                <p className="text-charcol text-base sm:text-lg lg:text-xl mb-6 italic line-clamp-6 sm:line-clamp-none">
                  "{testimonials[currentIndex].content}"
                </p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center justify-center"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-earthybeige rounded-full flex items-center justify-center text-white font-bold text-lg sm:text-xl">
                    {testimonials[currentIndex].name[0]}
                  </div>
                  <div className="ml-3 sm:ml-4 text-left">
                    <p className="font-semibold text-charcol text-sm sm:text-base">{testimonials[currentIndex].name}</p>
                    <p className="text-sage text-xs sm:text-sm">Therapy Client</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <button
          className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white p-1 sm:p-2 rounded-full shadow-lg text-charcol hover:text-golden-yellow transition-colors focus:outline-none focus:ring-2 focus:ring-golden-yellow"
          onClick={() => paginate(-1)}
          aria-label="Previous testimonial"
        >
          <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6" />
        </button>
        <button
          className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white p-1 sm:p-2 rounded-full shadow-lg text-charcol hover:text-golden-yellow transition-colors focus:outline-none focus:ring-2 focus:ring-golden-yellow"
          onClick={() => paginate(1)}
          aria-label="Next testimonial"
        >
          <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
        </button>
      </div>
    </section>
  );
};

export default TestimonialsSection;
